import React from 'react';
import { useMutation } from '@apollo/client';
import {
  IDashboardFolderDeleteMutation,
  IDashboardFolderDeleteMutationVariables,
  ISubFolderFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { dashboardFolderDeleteMutation } from '../../../../utils/apollo/dashboards';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectModalData } from '../../../../redux/selectors/modal';
import { assertNotNil } from '../../../../utils/types';
import { closeModal } from '../../../../redux/slices/modal';
import { deleteSubFolder } from '../../../../redux/slices/dashboards/dashboardInstance';
import toastMessage from '../../../../utils/toastMessage';

const DashboardFolderDeleteModal = () => {
  const dispatch = useAppDispatch();

  const dashboardFolder: ISubFolderFieldsFragment =
    useAppSelector(selectModalData);
  assertNotNil(dashboardFolder);

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleComplete = ({
    dashboardFolderDelete,
  }: IDashboardFolderDeleteMutation) => {
    const { errors, message } = dashboardFolderDelete;
    if (printGraphqlErrors(errors)) {
      return;
    }
    assertNotNil(message);

    dispatch(
      deleteSubFolder({
        subFolderId: dashboardFolder.id,
      }),
    );
    handleClose();
    toastMessage({
      message: `${dashboardFolder.name} folder has been deleted`,
      type: 'info',
    });
  };

  const [dashboardFolderDelete, { loading }] = useMutation<
    IDashboardFolderDeleteMutation,
    IDashboardFolderDeleteMutationVariables
  >(dashboardFolderDeleteMutation, {
    onCompleted: handleComplete,
  });

  const handleSubmit = () => {
    dashboardFolderDelete({
      variables: {
        input: {
          folderId: dashboardFolder.id,
        },
      },
    });
  };

  return (
    <ConfirmationModal
      open
      onClose={handleClose}
      onSubmit={handleSubmit}
      header={'Delete Confirmation'}
      confirmActionLabel={'Delete'}
      disabled={loading}
      trackContext="dashboards"
      trackConfirmAction="delete folder - confirm"
      trackCancelAction="delete folder - cancel"
    >
      Do you want to delete Dashboard Folder{' '}
      <strong>{dashboardFolder.name}</strong>? This action cannot be undone.
    </ConfirmationModal>
  );
};

export default DashboardFolderDeleteModal;
